import * as React from "react";
import { RouteComponentProps } from "@reach/router";
import { cx, css } from "@emotion/css";
import { produce } from "immer";
import { FormikProps, Field } from "formik";
import moment from "moment";
import { navigate } from "../support/hashRouting";
import { FormValues } from "../support/FormValues";
import { StepHeader } from "../components/StepHeader";
import Breadcrumbs from "../components/Breadcrumbs";
import { PfFundSelection } from "../components/PfFundSelection";
import bootstrapStyles from "../css/bootstrap.module.css";
import formGroupStyles from "../css/formgroup.module.css";
import { GeneralButton } from "../components/GeneralButton";
import { FieldNameEnum } from "../support/FieldNameEnum";
import { FieldSwitch } from "../components/FieldSwitch";
import {
  OCPFundEntryInterface,
  BYOFundCustomPercentEntryInterface,
  BYOFundEntryInterface
} from "../support/interfaces/fundSelectionInterfaces";
import {
  fundEnumList,
  fundPercentEnumList,
  fundCodeEnumList
} from "../support/BYOFundEnums";
import { fundsApdatper, FundsCome } from "../support/adapters/fundsAdapter";
import { AccountTypeEnum } from "../support/AccountTypeEnum";
import { useScrollTop } from "../customHooks/scrollTop";

interface FundSelectionProps extends RouteComponentProps {
  formikBag: FormikProps<FormValues>;
  fundList: FundsCome;
}

const linkStyles = css`
  margin: 16px 0;
  padding: 0;
  text-align: center;
  li {
    list-style-type: none;
    display: inline-block;
    padding: 0 15px;
    &:first-of-type {
      border-right: 1px solid #ccc;
    }
    a {
      text-decoration: underline;
    }
  }
`;
const disclosureLinks = css`
  margin-top: 32px;
`;

const FundSelection = ({
  formikBag: { values, errors, touched, setFieldValue, setFieldTouched },
  location,
  fundList
}: FundSelectionProps) => {
  useScrollTop(location);
  let funds: any;
  if (fundList.byoFundList) {
    funds = fundsApdatper(fundList);
  }

  const stepHeaderText = `Thanks, ${values.firstName}! We're almost done.`;
  const stepNote = "";

  const getFundBucketByBirthYear = (
    year: number,
    OCPFunds: OCPFundEntryInterface[]
  ): OCPFundEntryInterface => {
    const actualYear = Number.isNaN(year) ? Number.MIN_SAFE_INTEGER : year;

    const filteredArray = OCPFunds.filter(element => {
      const lowerBound = element.yearLowerBound
        ? element.yearLowerBound
        : Number.MIN_SAFE_INTEGER;
      const upperBound = element.yearUpperBound
        ? element.yearUpperBound
        : Number.MAX_SAFE_INTEGER;

      return actualYear >= lowerBound && actualYear <= upperBound;
    });

    if (filteredArray.length === 0) {
      return OCPFunds[OCPFunds.length - 1];
    }

    return filteredArray[0];
  };

  const onOCPFundSelectionChanged = React.useCallback(
    (fund: OCPFundEntryInterface): void => {
      setFieldValue(FieldNameEnum.fund1, fund.fundName);
      setFieldValue(FieldNameEnum.fund1Code, fund.fundCode);
      setFieldValue(FieldNameEnum.fund1Percent, 100);
    },
    [setFieldValue]
  );

  const initialChoicesArray = React.useMemo(() => {
    const res: BYOFundCustomPercentEntryInterface[] = [];
    if (!values.byo) {
      return [{ fundName: "", fundCode: "", fundPercent: undefined }];
    }
    fundEnumList.forEach((fund, index) => {
      if (values[fund] === "") {
        return;
      }
      res.push({
        fundName: values[fund] as string,
        fundCode: values[fundCodeEnumList[index]] as string,
        fundPercent: values[fundPercentEnumList[index]] as number
      });
    });
    return res.length !== 0
      ? res
      : [{ fundName: "", fundCode: "", fundPercent: undefined }];
  }, [values]);

  const [choicesArray, setChoicesArray] = React.useState<
    BYOFundCustomPercentEntryInterface[]
  >(initialChoicesArray);

  const [pageValid, setPageValid] = React.useState(true);

  const clearFunds = React.useCallback(
    (startIndex = 0) => {
      for (let i = startIndex; i < fundEnumList.length; i += 1) {
        setFieldValue(fundEnumList[i], "");
        setFieldValue(fundCodeEnumList[i], "");
        setFieldValue(fundPercentEnumList[i], "");
        setFieldTouched(fundEnumList[i], false);
        setFieldTouched(fundPercentEnumList[i], false);
      }
    },
    [setFieldValue, setFieldTouched]
  );

  // React.useEffect(() => {
  //   clearFunds();
  // }, [values.transferToSame]);

  React.useEffect(() => {
    if (!values.transferToSame) {
      if (values.byo) {
        choicesArray.forEach((choice, index) => {
          setFieldValue(fundEnumList[index], choice.fundName);
          setFieldValue(fundCodeEnumList[index], choice.fundCode);
          setFieldValue(fundPercentEnumList[index], choice.fundPercent || "");
          setFieldTouched(fundEnumList[index]);
          setFieldTouched(fundPercentEnumList[index]);
        });

        const startIndex = choicesArray.length;
        clearFunds(startIndex);
      } else {
        clearFunds();
        onOCPFundSelectionChanged(
          getFundBucketByBirthYear(
            moment(values.birthDate, "MM/DD/YYYY").year(),
            funds.ocp
          )
        );
        setPageValid(true);
      }
    } else {
      setFieldValue(FieldNameEnum.byo, false);
      setPageValid(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    choicesArray,
    setFieldValue,
    setFieldTouched,
    clearFunds,
    values.transferToSame,
    values.byo,
    values.birthDate,
    onOCPFundSelectionChanged
  ]);

  const onFundTypeChange: React.ChangeEventHandler<HTMLSelectElement> = e => {
    setFieldValue(FieldNameEnum.byo, e.currentTarget.value === "byo");
  };

  const BYOOnAddFundClicked = React.useCallback(() => {
    setChoicesArray(prevState =>
      produce(prevState, draft => {
        draft.push({ fundName: "", fundCode: "", fundPercent: undefined });
      })
    );
  }, []);

  const BYOOnFundSelectionChange = React.useCallback(
    (fund: BYOFundEntryInterface | undefined, index: number) => {
      setChoicesArray(prevState =>
        produce(prevState, draft => {
          draft[index].fundName = fund ? fund.fundName : "";
          draft[index].fundCode = fund ? fund.fundCode : "";
        })
      );
    },
    []
  );

  const BYOOnPercentChange = React.useCallback(
    (e: React.FormEvent<HTMLInputElement>, index: any) => {
      const {
        currentTarget: { value }
      } = e;
      setChoicesArray(prevState =>
        produce(prevState, draft => {
          draft[index].fundPercent = parseInt(value, 10);
        })
      );
    },
    []
  );

  const BYOOnRemoveEntry = React.useCallback((index: any) => {
    setChoicesArray(prevState =>
      produce(prevState, draft => {
        draft.splice(index, 1);
      })
    );
  }, []);

  return (
    <div>
      <Breadcrumbs currentStep={3} />
      <div className={bootstrapStyles.row}>
        <div
          className={cx(
            bootstrapStyles["col-md-12"],
            formGroupStyles.contentPadding
          )}
        >
          <StepHeader stepHeader={stepHeaderText} stepNote={stepNote} />
        </div>
      </div>
      <div className={bootstrapStyles.row}>
        <div
          className={cx(
            bootstrapStyles["col-md-12"],
            formGroupStyles.contentPadding
          )}
        />
      </div>

      <div className={cx(bootstrapStyles.row)} style={{ marginBottom: "25px" }}>
        <div
          className={bootstrapStyles["col-md-12"]}
          aria-label="Should we invest your rollover/transfer according to your current investment election?"
        >
          <Field
            name={FieldNameEnum.transferToSame}
            id={FieldNameEnum.transferToSame}
            label="Should we invest your rollover/transfer according to your current investment election?"
            component={FieldSwitch}
            onClick={() => {
              clearFunds();
            }}
          />
        </div>
      </div>
      {!values.transferToSame && (
        <div className={cx(bootstrapStyles.row)}>
          <div className={bootstrapStyles["col-md-12"]}>
            <PfFundSelection
              onFundTypeChange={onFundTypeChange}
              onOCPFundSelectionChanged={onOCPFundSelectionChanged}
              funds={funds}
              externalBindedOCPTargetValue={values.fund1}
              fundSelectionType={values.byo ? "byo" : "ocp"}
              birthYear={moment(values.birthDate, "MM/DD/YYYY").year()}
              BYOfundsMaxNumber={4}
              BYOfundChoices={choicesArray}
              BYOOnAddFund={BYOOnAddFundClicked}
              onFundSelectionChange={BYOOnFundSelectionChange}
              onPercentChange={BYOOnPercentChange}
              onRemoveEntry={BYOOnRemoveEntry}
              validHandler={isValid => {
                setPageValid(isValid);
              }}
            />
          </div>
        </div>
      )}

      {!values.transferToSame && (
        <div className="row">
          <div className="col-md-12 contentPadding">
            <div id="fundSelectionYourSimpleIra">
              {values.toAccountType === AccountTypeEnum.SEPIRA && (
                <p>
                  <b>
                    Your SEP assets are held in a custodial account that charges
                    a $15.00 per fund annual fee. The custodial fee is waived if
                    your eligible investments with American Century Investments
                    total $10,000 or more.
                  </b>
                </p>
              )}
              <div className={disclosureLinks}>
                <div className={bootstrapStyles["col-md-12"]}>
                  <ul className={linkStyles}>
                    <li>
                      <a
                        href="https://www.americancentury.com/invest/funds/?t=perf&shareClass=Investor+Class&type=Mutual+Fund"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Prospectus
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.americancentury.com/invest/funds/?t=documents&shareClass=Investor+Class&type=Mutual+Fund"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Fund Information
                      </a>
                    </li>
                  </ul>
                  <ul className={linkStyles}>
                    <li>
                      <a
                        href="https://res.cloudinary.com/americancentury/image/upload/partnership/simple_disclosure_statement.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        SIMPLE IRA Custodial-Agreement
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://res.cloudinary.com/americancentury/image/upload/docs/disclosure_statement.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Traditional, Roth and SEP IRA Custodial-Agreement
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <p />
            </div>
            {!values.byo && (
              <div id="fundSelectionTimeAdjustedBottom">
                <p>
                  A One Choice Target Date Portfolio&apos;s target date is the
                  approximate year when investors plan to retire or start
                  withdrawing their money. The principal value of the investment
                  is not guaranteed at any time, including at the target date.
                </p>
                <p>
                  Each target-date One Choice Target Date Portfolio seeks the
                  highest total return consistent with American Century
                  Investments&apos; proprietary asset mix. Over time, the asset
                  mix and weightings are adjusted to be more conservative. In
                  general, as the target year approaches, the portfolio&apos;s
                  allocation becomes more conservative by decreasing the
                  allocation to stocks and increasing the allocation to bonds
                  and money market instruments.
                </p>
                <p>
                  If you would like help choosing your investments, please call
                  American Century Investments at 1-844-426-2368.
                </p>
              </div>
            )}
            <div id="fundSelectionUniversalBottom">
              <p>
                <b>
                  You should consider the fund&apos;s investment objectives,
                  risks, charges and expenses carefully before you invest. The
                  fund&apos;s{" "}
                  <a
                    href="https://www.americancentury.com/content/direct/en/products/all-funds/r.html#!/documents"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    prospectus or summary prospectus
                  </a>
                  , which can be obtained by calling American Century
                  Investments at 1-844-426-2368, contains this and other
                  information about the fund, and should be read carefully
                  before investing. Investments are subject to market risk.
                </b>
                <br />
              </p>
              <p>
                American Century Investment Services, Inc. Distributor
                <br />
                &copy; Copyright {moment().year()} American Century Proprietary
                Holdings, Inc. All Rights reserved. All dates and times are
                based on Central time.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className={bootstrapStyles.row}>
        <div className={bootstrapStyles["col-md-12"]}>
          <hr />
        </div>
      </div>

      <div className={bootstrapStyles.row}>
        <div className={cx(bootstrapStyles["col-md-12"])}>
          <GeneralButton
            id="back"
            text="back"
            buttonType="secondary"
            onClick={() => {
              navigate("/personalInformation");
            }}
          />
          <div
            className={css`
              float: right;
            `}
          >
            <GeneralButton
              id="continue"
              text="continue"
              onClick={() => {
                navigate("/review");
              }}
              buttonType="primary"
              disabled={!pageValid}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundSelection;
